import React, { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { Helmet } from 'react-helmet';

import './styles/ZendeskWidget.scss';

const ZENDESK_SRC =
  'https://static.zdassets.com/ekr/snippet.js?key=a90fa090-077d-43c5-9db8-0a1f87d28536';

export const useZendeskChatStatus = createUseZendeskChatStatus(false);

/** Used to load the Zendesk chat script into the document head via Helmet */
export default function ZendeskWidget() {
  const [scriptLoaded, setScriptLoaded] = useState<boolean>(
    typeof (window as any)?.zE === 'function'
  );
  const [_, setChatOnlineStatus] = useZendeskChatStatus();

  // Register Zendesk event handlers
  useEffect(() => {
    if (typeof (window as any)?.zE === 'function') {
      (window as any)?.zE('webWidget:on', 'chat:connected', handleChatConnected);
      (window as any)?.zE('webWidget:on', 'chat:status', handleChatStatusChanged);
      (window as any)?.zE('webWidget:on', 'chat:unreadMessages', handleUnreadMessages);
    }
  }, [scriptLoaded]);

  function handleChatConnected() {
    const departments: Department[] | undefined = (window as any)?.zE(
      'webWidget:get',
      'chat:departments'
    );

    // Are any of the coaching "departments" online
    //
    // We are using departments to represent different types of issues that the customer might be facing.
    // Agents are assigned to one or more departments depending on the kind of issue that they respond to.
    //
    // For the below logic, if an agent of any issue type is online, we currently handle that as all of
    // ZD Chat being online and ready to accept new chats
    const anyOnline = departments?.some(department => department.status === 'online') ?? false;
    setChatOnlineStatus(anyOnline);
  }

  function handleChatStatusChanged(status: ChatOnlineStatus) {
    if (status === 'online' || status === 'away') {
      setChatOnlineStatus(true);
    } else {
      setChatOnlineStatus(false);
    }
  }

  function handleUnreadMessages(unreadMessageCount: number) {
    // Force the chat window open when a new message is received from the agent
    //
    // Although this may not be perfect UX - it prevents the end user from losing
    // the chat window since we are hiding the default launcher
    if (unreadMessageCount > 0) {
      (window as any).zE('webWidget', 'open');
    }
  }

  function handleChangeClientState(_: any, addedTags: Tags) {
    if (addedTags?.scriptTags?.length) {
      const foundScript = addedTags.scriptTags.find(attributes => attributes?.src === ZENDESK_SRC);
      if (foundScript) {
        foundScript.addEventListener('load', () => setScriptLoaded(true), { once: true });
      }
    }
  }

  return (
    // @ts-ignore onChangeClientState callback is improperly typed by DefinitelyTyped
    // onChangeClientState lets us known when a helmet managed script has been added to the dom
    <Helmet onChangeClientState={handleChangeClientState}>
      <script id="ze-snippet" src={ZENDESK_SRC} />
    </Helmet>
  );
}

/** Opens the Zendesk chat widget even if we are outside of chat hours */
export function openWidget() {
  if (typeof (window as any)?.zE === 'function') {
    (window as any).zE('webWidget', 'open');
  }
}

function createUseZendeskChatStatus(assumeChatIsOnline: boolean) {
  let listeners: Dispatch<SetStateAction<boolean>>[] = [];
  let isChatOnline = assumeChatIsOnline;

  function setIsOnline(online: boolean) {
    isChatOnline = online;
    listeners.forEach(listener => {
      listener(online);
    });
  }

  /** Returns true or false based on whether Zendesk live chat is currently online
   * along with a setter for setting the status of live chat
   */
  function useZendeskChatStatus(): UseZendeskChatStatusReturnValues {
    const [localIsChatOnline, isChatOnlineListener] = useState<boolean>(isChatOnline);
    useEffect(() => {
      listeners.push(isChatOnlineListener);

      return () => {
        listeners = listeners.filter(listener => listener !== isChatOnlineListener);
      };
    }, []);

    return [localIsChatOnline, setIsOnline];
  }

  return useZendeskChatStatus;
}

type SetStateFunc = (isLive: boolean) => void;
type UseZendeskChatStatusReturnValues = [boolean, SetStateFunc];

type Department = {
  name: string;
  status: ChatOnlineStatus;
  id: number;
};
type ChatOnlineStatus = 'online' | 'away' | 'offline';

type Tags = {
  scriptTags: HTMLScriptElement[];
};
